import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { usePortalAuth } from '../../store/portal-auth';
import { portalTickets } from '../../lib/portal-api';
import { clsx } from 'clsx';

const STATUS_LABELS: Record<string, string> = {
  NY: 'Ny', UNDER_ARBEID: 'Under arbeid', VENTER_KUNDE: 'Venter kunde',
  VENTER_INTERNT: 'Venter internt', LOST: 'Løst', LUKKET: 'Lukket',
};
const STATUS_COLORS: Record<string, string> = {
  NY: 'bg-blue-100 text-blue-700', UNDER_ARBEID: 'bg-amber-100 text-amber-700',
  VENTER_KUNDE: 'bg-purple-100 text-purple-700', VENTER_INTERNT: 'bg-orange-100 text-orange-700',
  LOST: 'bg-green-100 text-green-700', LUKKET: 'bg-slate-100 text-slate-600',
};

function formatSakNr(n: number): string { return `SAK-${String(n).padStart(4, '0')}`; }
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function PortalTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = usePortalAuth();
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: ticket, isLoading } = useQuery({
    queryKey: ['portal-ticket', id],
    queryFn: () => portalTickets.get(id!),
    enabled: !!id,
    refetchInterval: 15_000,
  });
  const commentMutation = useMutation({
    mutationFn: () => portalTickets.addComment(id!, comment.trim()),
    onSuccess: () => { setComment(''); setError(null); qc.invalidateQueries({ queryKey: ['portal-ticket', id] }); qc.invalidateQueries({ queryKey: ['portal-tickets'] }); },
    onError: (err: any) => { setError(err?.message ?? 'Kunne ikke sende kommentar.'); },
  });
  const isClosed = ticket?.status === 'LUKKET' || ticket?.status === 'LOST';

  if (isLoading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><span className="animate-pulse text-slate-400">Laster sak…</span></div>;
  if (!ticket) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 gap-3">
      <p className="text-slate-500">Sak ikke funnet.</p>
      <button onClick={() => navigate('/portal')} className="btn-primary text-sm">Tilbake til mine saker</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/portal')} className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>
            Mine saker
          </button>
          <button onClick={logout} className="btn-ghost text-sm">Logg ut</button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-slate-400">{formatSakNr(ticket.sakNr)}</span>
            <span className={clsx('chip text-xs', STATUS_COLORS[ticket.status])}>{STATUS_LABELS[ticket.status]}</span>
          </div>
          <h1 className="mt-2 text-xl font-semibold text-slate-800">{ticket.tittel}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span>Opprettet {formatDate(ticket.opprettet)}</span>
            {ticket.kategori && <span>· {ticket.kategori}</span>}
          </div>
        </div>
        <div className="space-y-4">
          {ticket.messages.map((msg) => {
            const isFromCustomer = msg.type === 'KUNDE_MELDING';
            return (
              <div key={msg.id} className={clsx('flex', isFromCustomer ? 'justify-end' : 'justify-start')}>
                <div className={clsx('max-w-[80%] rounded-2xl px-4 py-3', isFromCustomer ? 'bg-brand-500 text-white' : 'bg-white border border-slate-200 text-slate-700')}>
                  <div className="mb-1 flex items-center gap-2 text-xs opacity-70">
                    <span>{isFromCustomer ? 'Deg' : 'Support'}</span>
                    <span>· {formatDate(msg.opprettet)}</span>
                  </div>
                  {msg.innholdHtml ? (
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: msg.innholdHtml }} />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{msg.innholdTekst}</p>
                  )}
                  {msg.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.attachments.map((a) => (
                        <span key={a.id} className={clsx('rounded-lg px-2 py-1 text-xs', isFromCustomer ? 'bg-white/20' : 'bg-slate-100 text-slate-600')}>{a.filnavn}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {isClosed ? (
          <div className="mt-6 rounded-xl bg-slate-100 p-4 text-center text-sm text-slate-500">Denne saken er lukket. Opprett en ny sak for videre henvendelser.</div>
        ) : (
          <div className="mt-6 card p-4">
            {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Skriv en melding til support…" rows={4} className="input resize-y" />
            <div className="mt-3 flex justify-end">
              <button onClick={() => comment.trim() && commentMutation.mutate()} disabled={commentMutation.isPending || !comment.trim()} className="btn-primary text-sm">{commentMutation.isPending ? 'Sender…' : 'Send melding'}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
